import { useState } from 'react';

import { Button, Input, Select } from './Input2';
import { Arrows, General } from './StrokeIcons';
import { Tooltip } from './Tooltip';
import { WaveSpinner } from './WaveSpinner';
import { IdentifierRow } from './IdentifierRow';

export type LinkedInMapping = {
  id: number;
  foreignSource: 'linkedin' | 'salesnav' | string;
  foreignId: string | null;
  createdAt: Date;
  lastFailedAt: Date | null;
};

export type LinkedInIdentifiersSectionPureProps = {
  // Data
  mappings: LinkedInMapping[];
  linkedinUnavailable?: boolean;
  isLoading: boolean;

  // Callbacks
  onDelete: (mappingId: number) => void;
  onVerify: (mappingId: number) => void;
  onCopy: (foreignId: string) => void;
  onAdd: (foreignSource: 'linkedin' | 'salesnav', foreignId: string) => void;

  // Loading states
  isDeleting: boolean;
  isVerifying: boolean;
  isAdding: boolean;
};

export function LinkedInIdentifiersSectionPure({
  mappings,
  linkedinUnavailable,
  isLoading,
  onDelete,
  onVerify,
  onCopy,
  onAdd,
  isDeleting,
  isVerifying,
  isAdding: isAddingPending,
}: LinkedInIdentifiersSectionPureProps) {
  const [newSource, setNewSource] = useState<'linkedin' | 'salesnav'>('salesnav');
  const [newIdentifier, setNewIdentifier] = useState('');
  const [isAddingForm, setIsAddingForm] = useState(false);
  // Collapsed by default — most users don't care about LinkedIn URN/SalesNav IDs day-to-day.
  // Auto-expand if a mapping is failing or unavailable so they don't miss the alert.
  const hasFailingMapping = mappings.some(
    (m) => m.lastFailedAt && m.createdAt && new Date(m.lastFailedAt) > new Date(m.createdAt),
  );
  const [isExpanded, setIsExpanded] = useState(hasFailingMapping || Boolean(linkedinUnavailable));

  // Filter to only show linkedin and salesnav mappings
  const linkedInMappings = mappings.filter(
    (m) => m.foreignSource === 'linkedin' || m.foreignSource === 'salesnav',
  );

  const handleAdd = () => {
    if (!newIdentifier.trim()) return;
    onAdd(newSource, newIdentifier.trim());
    setNewIdentifier('');
    setIsAddingForm(false);
  };

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      {/* Header — clickable to expand/collapse */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex flex-1 items-center gap-1.5 text-left"
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}>
          {isExpanded ? (
            <Arrows.ChevronDown className="size-3.5 text-gray-400" />
          ) : (
            <Arrows.ChevronRight className="size-3.5 text-gray-400" />
          )}
          <span className="text-sm font-semibold text-gray-900 dark:text-white">LinkedIn Identifiers</span>
          {!isExpanded && linkedInMappings.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              ({linkedInMappings.length})
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {linkedinUnavailable && (
            <Tooltip label="This contact could not be found on LinkedIn. Add a valid identifier to reactivate.">
              <span className="rounded-sm bg-red-50 px-2 py-0.5 text-xs text-red-500 dark:bg-red-900/30">
                Unavailable
              </span>
            </Tooltip>
          )}
          {isExpanded && !isAddingForm && (
            <Button
              size="xs"
              text
              icon={<General.Plus className="size-3.5" />}
              onClick={() => setIsAddingForm(true)}
              tooltip="Add identifier"
            />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="mt-2">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <WaveSpinner className="size-4" />
            </div>
          )}

          {/* List of identifiers */}
          {!isLoading && linkedInMappings.length > 0 && (
            <div className="mb-2 flex flex-col">
              {linkedInMappings.map((mapping) => (
                <IdentifierRow
                  key={mapping.id}
                  foreignSource={mapping.foreignSource}
                  foreignId={mapping.foreignId}
                  createdAt={mapping.createdAt}
                  lastFailedAt={mapping.lastFailedAt}
                  onDelete={() => onDelete(mapping.id)}
                  onVerify={() => onVerify(mapping.id)}
                  onCopy={() => onCopy(mapping.foreignId ?? '')}
                  isDeleting={isDeleting}
                  isVerifying={isVerifying}
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && linkedInMappings.length === 0 && !isAddingForm && (
            <p className="py-2 text-xs text-gray-400 dark:text-gray-500">No identifiers found</p>
          )}

          {/* Add new identifier form */}
          {isAddingForm && (
            <div className="flex flex-col gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
              <div className="flex gap-2">
                <Select
                  size="sm"
                  value={newSource}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewSource(e.target.value as 'linkedin' | 'salesnav')}
                  className="w-28"
                  options={[
                    { value: 'salesnav', label: 'SalesNav' },
                    { value: 'linkedin', label: 'LinkedIn' },
                  ]}
                />
                <Input
                  size="sm"
                  placeholder={newSource === 'salesnav' ? 'SalesNav ID (39 chars)' : 'Username or URN ID'}
                  value={newIdentifier}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewIdentifier(e.target.value)}
                  className="flex-1 font-mono text-xs"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  size="xs"
                  text
                  onClick={() => {
                    setIsAddingForm(false);
                    setNewIdentifier('');
                  }}>
                  Cancel
                </Button>
                <Button size="xs" onClick={handleAdd} disabled={isAddingPending || !newIdentifier.trim()}>
                  {isAddingPending ? <WaveSpinner className="size-3" /> : 'Add'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LinkedInIdentifiersSectionPure;
